import { getCurrencySymbol } from './getCurrencySymbol';
import i18next from 'i18next';

/**
 * Форматирует число-цену компактно:
 * - < 1000: как есть (999 → '999')
 * - ≥ 1 000 000: миллионы (1000000 → '1М'/'1M', 1500000 → '1.5М'/'1.5M')
 * - ≥ 1000: тысячи (1000 → '1т'/'1k', 1500 → '1.5т'/'1.5k')
 * - full: true — без сокращения, с пробелом-разделителем (10000 → '10 000')
 *
 * @param value — числовая цена
 * @param options.full — если true, выводит полное число без сокращения
 *
 * @example
 * formatPriceNumber(500)                          // '500'
 * formatPriceNumber(1000)                         // '1т' (если язык ru)
 * formatPriceNumber(1000000)                      // '1М'
 * formatPriceNumber(10000, { full: true })        // '10 000'
 */
export function formatPriceNumber(
  value: number,
  options?: { full?: boolean },
): string {
  // Полный формат — пробел-разделитель тысяч от 5 цифр
  if (options?.full) {
    const str = String(value);
    return Math.abs(value) >= 10000
      ? str.replace(/\B(?=(\d{3})+(?!\d))/g, '\u00A0')
      : str;
  }

  const lang = i18next.language || 'ru';

  // Миллионы
  if (value >= 1_000_000) {
    const short = value / 1_000_000;
    const formatted = short % 1 === 0 ? String(short) : short.toFixed(1);
    const suffix = lang === 'ru' ? 'М' : 'M';
    return `${formatted}${suffix}`;
  }

  // Тысячи
  if (value >= 1000) {
    const short = value / 1000;
    const formatted = short % 1 === 0 ? String(short) : short.toFixed(1);
    const suffix = lang === 'ru' ? 'т' : 'k';
    return `${formatted}${suffix}`;
  }

  return String(value);
}

/**
 * Форматирует цену с символом валюты: «$1k», «₽10т»
 * С full: true — полный формат: «$1000», «₽10 000»
 *
 * @param price — числовая цена
 * @param currency — ISO-код валюты (USD, RUB, ...)
 * @param options.full — если true, выводит полное число без сокращения
 * @returns отформатированная строка или null если цена пустая/нулевая
 */
export function formatPriceWithCurrency(
  price: number | null | undefined,
  currency?: string | null,
  options?: { full?: boolean },
): string | null {
  if (price == null || price <= 0) return null;
  return `${getCurrencySymbol(currency)}${formatPriceNumber(price, options)}`;
}